import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { APPLICANT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointpersonalDetails";
import { RootState } from "app/lib/store/store";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { JointPersonalFormTwoData } from "app/utils/types";
import DynamicFormTwo from "app/components/Individual/personalDetails/dynamicForms/DynamicFormTwo";

const PartTwo: React.FC = () => {
  const dispatch = useDispatch();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formTwo, ...others } = useSelector(
    (state: RootState) => state.jointpersonalDetails.stepsData
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const { mutateAsync: submitPersonalDetails, isPending } =
    usePersonalDetails();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<JointPersonalFormTwoData>({
    defaultValues: {
      yourDetails: {
        knownByOtherName: "no",
      },
      coBorrowerDetails: {
        knownByOtherName: "no",
      },
    },
  });
  const applicantName = watch("yourDetails.knownByOtherName");
  const coapplicantName = watch("coBorrowerDetails.knownByOtherName");

  const onSubmit = async (data: JointPersonalFormTwoData) => {
    const { yourDetails, coBorrowerDetails } = data || {};
    if (!savedIds?.secondApplicantID) {
      toast.error("Applicant ID not found!");
      return;
    }

    const yourDetailsData = {
      ...yourDetails,
      dateOfBirth: yourDetails?.dateOfBirth,
      knownByOtherName: yourDetails.knownByOtherName === "yes" ? true : false,
      drivingLicense: {
        ...yourDetails.drivingLicense,
        expiry: yourDetails?.drivingLicense?.expiry,
      },
    };
    const coBorrowerDetailsData = {
      ...coBorrowerDetails,
      dateOfBirth: coBorrowerDetails.dateOfBirth,
      knownByOtherName:
        coBorrowerDetails.knownByOtherName === "yes" ? true : false,
      drivingLicense: {
        ...coBorrowerDetails.drivingLicense,
        expiry: coBorrowerDetails.drivingLicense?.expiry,
      },
    };

    const request1 = {
      url: `${APPLICANT}/${savedIds?.applicantID}/second`,
      data: yourDetailsData,
    };

    const request2 = {
      url: `${APPLICANT}/${savedIds?.secondApplicantID}/second`,
      data: coBorrowerDetailsData,
    };

    const storedData = {
      yourDetails: yourDetailsData,
      coBorrowerDetails: coBorrowerDetailsData,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitPersonalDetails(request1),
        submitPersonalDetails(request2),
      ]);
      if (response1?.success && response2?.success) {
        dispatch(nextStep());
        dispatch(
          addStepsData({
            ...others,
            formTwo: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formTwo) {
      reset({
        ...formTwo,
        yourDetails: {
          ...formTwo.yourDetails,
          knownByOtherName: formTwo.yourDetails.knownByOtherName ? "yes" : "no",
        },
        coBorrowerDetails: {
          ...formTwo.coBorrowerDetails,
          knownByOtherName: formTwo.coBorrowerDetails.knownByOtherName
            ? "yes"
            : "no",
        },
      });
    } else {
      const { YourDetails, Coborrower } = applicant || {};
      if (YourDetails?.dateOfBirth) {
        setValue("yourDetails.dateOfBirth", YourDetails.dateOfBirth);
      }
      if (Coborrower?.dateOfBirth) {
        setValue("coBorrowerDetails.dateOfBirth", Coborrower.dateOfBirth);
      }
    }
  }, [formTwo, reset]);

  return (
    <>
      <ProgressBar progress={25} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
        {/* <SubTitle title="Please enter you loan and baloon amount" /> */}
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <DynamicFormTwo
                control={control}
                errors={errors}
                applicantName={applicantName as string}
                knownByOtherName="yourDetails.knownByOtherName"
                otherNameDesc="yourDetails.otherNameDesc"
                stateOfIssue="yourDetails.drivingLicense.stateOfIssue"
                drivingLicense="yourDetails.drivingLicense.number"
                dateOfBirth="yourDetails.dateOfBirth"
                expiry="yourDetails.drivingLicense.expiry"
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />

              <DynamicFormTwo
                control={control}
                errors={errors}
                applicantName={coapplicantName as string}
                knownByOtherName="coBorrowerDetails.knownByOtherName"
                otherNameDesc="coBorrowerDetails.otherNameDesc"
                stateOfIssue="coBorrowerDetails.drivingLicense.stateOfIssue"
                drivingLicense="coBorrowerDetails.drivingLicense.number"
                dateOfBirth="coBorrowerDetails.dateOfBirth"
                expiry="coBorrowerDetails.drivingLicense.expiry"
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartTwo;
